import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/api';
import { Plus, Loader2, Wrench, Search, Trash2, List, Columns3 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import FilterBar, { FilterSelect, FilterInput, FilterDate } from '@/components/FilterBar';
import { STATUS_COLORS, REPAIR_STATUSES, REPAIR_PIPELINE_STAGES, STATUS_LABELS, formatStatus } from '@/constants/statusColors';
import useRepairCategories from '@/hooks/useRepairCategories';

const STATUSES = REPAIR_STATUSES;
const PRIORITIES = ['emergency','high','medium','low'];
const STALE_OPTS = [
  { value: '4', label: 'No update 4h+' },
  { value: '8', label: 'No update 8h+' },
  { value: '24', label: 'No update 24h+' },
  { value: '48', label: 'No update 48h+' },
  { value: '72', label: 'No update 72h+' },
];

const PRIORITY_STRIP = { emergency:'strip-emergency', high:'strip-high', medium:'strip-medium', low:'strip-low' };

const EMPTY = { status:'', priority:'', category:'', vendor:'', unit_search:'', keyword:'', customer_id:'', assigned_operator_id:'', payment_state:'', date_from:'', date_to:'', stale_hours:'' };

export default function RepairEvents() {
  const { user } = useAuth();
  const [repairs, setRepairs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createCustomers, setCreateCustomers] = useState([]);
  const [createTrucks, setCreateTrucks] = useState([]);
  const [form, setForm] = useState({ customer_id:'', truck_id:'', priority:'medium', category:'Other', additional_categories: [], issue_description:'', location:'', vendor_name:'', assigned_vendor_id:'', mileage_at_repair:'' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const { names: CATS } = useRepairCategories();
  const [vendorList, setVendorList] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // View mode: 'list' (flat table) or 'pipeline' (grouped by stage)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('repairs_view_mode') || 'pipeline');
  // Quick preset: 'active' (default - hides done/paid), 'completed_paid', 'all'
  const [preset, setPreset] = useState(() => localStorage.getItem('repairs_preset') || 'active');

  // Apply preset filter on top of loaded repairs
  const visibleRepairs = useMemo(() => {
    if (preset === 'all') return repairs;
    if (preset === 'completed_paid') {
      return repairs.filter(r =>
        (r.status === 'completed' || r.status === 'invoice_created' || r.status === 'invoiced')
        && (r.payment_status === 'paid')
      );
    }
    // Default: 'active' — everything that still needs attention
    return repairs.filter(r => {
      const done = r.status === 'completed' || r.status === 'invoice_created' || r.status === 'invoiced';
      const paid = r.payment_status === 'paid';
      // Hide only if BOTH completed AND paid (truly closed)
      return !(done && paid);
    });
  }, [repairs, preset]);

  const presetCounts = useMemo(() => {
    let active = 0, completedPaid = 0;
    for (const r of repairs) {
      const done = r.status === 'completed' || r.status === 'invoice_created' || r.status === 'invoiced';
      const paid = r.payment_status === 'paid';
      if (done && paid) completedPaid += 1;
      else active += 1;
    }
    return { active, completed_paid: completedPaid, all: repairs.length };
  }, [repairs]);

  const setPresetWith = (p) => { setPreset(p); localStorage.setItem('repairs_preset', p); };

  // Renders the primary category + a "+N" pill when the repair has additional categories
  const CategoryCell = ({ repair }) => {
    const extras = repair.additional_categories || [];
    return (
      <span className="inline-flex items-center gap-1">
        <span>{repair.category || '—'}</span>
        {extras.length > 0 && (
          <span
            className="px-1.5 py-0.5 text-[9px] font-bold rounded-full font-['JetBrains_Mono']"
            style={{
              background: 'rgba(59, 130, 246, 0.15)',
              color: 'rgb(96, 165, 250)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
            }}
            title={extras.join(', ')}
            data-testid={`extra-cats-${repair.id}`}
          >
            +{extras.length}
          </span>
        )}
      </span>
    );
  };

  // Group repairs by status into pipeline columns (uses visibleRepairs)
  const repairsByStage = useMemo(() => {
    const map = {};
    REPAIR_PIPELINE_STAGES.forEach(s => { map[s.key] = []; });
    map._other = [];
    for (const r of visibleRepairs) {
      if (map[r.status] !== undefined) map[r.status].push(r);
      else map._other.push(r);
    }
    return map;
  }, [visibleRepairs]);

  const setView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('repairs_view_mode', mode);
  };

  const activeCount = Object.values(filters).filter(v => v !== '').length;

  useEffect(() => {
    // Load filter options — include both signed AND onboarded customers
    api.get('/customers', { params: { limit: 200, state: 'CONTRACT_SIGNED,ONBOARDED' } }).then(r => setCustomers(r.data.items || [])).catch(() => {});
    api.get('/users', { params: { role: 'operator' } }).then(r => setOperators(r.data || [])).catch(() => {});
    // Optional vendor picker for the New Repair modal — ops can pre-assign at creation
    // when they already know the shop. Leaving it on "— Assign later —" is fine; ops
    // can still type a free-text name (for unlisted shops) or assign on Repair Detail later.
    api.get('/vendors').then(r => setVendorList(r.data || [])).catch(() => setVendorList([]));
  }, []);

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''));
      params.limit = 50;
      const res = await api.get('/repairs', { params });
      setRepairs(res.data.items);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load repairs');
    } finally {
      setLoading(false);
    }
  };

  const setF = (key, val) => setFilters(p => ({ ...p, [key]: val }));
  const clearFilters = () => setFilters(EMPTY);

  const openCreate = async () => {
    try {
      const r = await api.get('/customers', { params: { limit: 100, state: 'CONTRACT_SIGNED,ONBOARDED' } });
      setCreateCustomers(r.data.items || []);
    } catch {}
    setShowCreate(true);
  };

  // Auto-open the "New Repair" modal when arriving with ?new=1 (from dashboard quick-action)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openCreate();
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrucks = async (cid) => {
    if (!cid) return setCreateTrucks([]);
    const r = await api.get('/trucks', { params: { customer_id: cid } });
    setCreateTrucks(r.data);
  };

  const loadAlerts = async (truckId, customerId) => {
    if (!truckId) return setRecentAlerts([]);
    try {
      const r = await api.get('/repairs/recent-alerts', { params: { truck_id: truckId, customer_id: customerId || undefined } });
      setRecentAlerts(r.data);
    } catch { setRecentAlerts([]); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/repairs', { ...form, mileage_at_repair: form.mileage_at_repair ? Number(form.mileage_at_repair) : undefined });
      toast.success('Repair event created!');
      setShowCreate(false);
      setForm({ customer_id:'', truck_id:'', priority:'medium', category:'Other', additional_categories: [], issue_description:'', location:'', vendor_name:'', assigned_vendor_id:'', mileage_at_repair:'' });
      navigate(`/ops/repairs/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/repairs/${deleteTarget.id}`);
      toast.success('Repair deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const fmt = (n) => n ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—';

  return (
    <div className="space-y-4 animate-fade-in" data-testid="repairs-page">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--text-faint)' }}>// REPAIR EVENTS</p>
          <h1 className="font-['Manrope'] font-extrabold text-2xl" style={{ color: 'var(--text-primary)' }}>
            Repairs
            {!loading && <span className="ml-3 text-sm font-['JetBrains_Mono'] font-normal" style={{ color: 'var(--text-faint)' }}>{visibleRepairs.length} of {total}</span>}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center rounded-md border" style={{ borderColor: 'var(--border)' }} data-testid="view-mode-toggle">
            <button onClick={() => setView('pipeline')}
              data-testid="view-pipeline"
              title="Pipeline view (grouped by stage)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors rounded-l-md"
              style={{
                background: viewMode === 'pipeline' ? 'var(--primary)' : 'transparent',
                color: viewMode === 'pipeline' ? '#fff' : 'var(--text-muted)',
              }}>
              <Columns3 size={12} /> Pipeline
            </button>
            <button onClick={() => setView('list')}
              data-testid="view-list"
              title="Flat list view"
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-colors rounded-r-md border-l"
              style={{
                background: viewMode === 'list' ? 'var(--primary)' : 'transparent',
                color: viewMode === 'list' ? '#fff' : 'var(--text-muted)',
                borderColor: 'var(--border)',
              }}>
              <List size={12} /> List
            </button>
          </div>
          <button onClick={openCreate} data-testid="create-repair-btn"
            className="btn-primary flex items-center gap-2 text-sm uppercase tracking-wide">
            <Plus size={14} /> New Repair
          </button>
        </div>
      </div>

      {/* Quick Preset Chips */}
      <div className="flex items-center gap-2" data-testid="repair-presets">
        {[
          { key: 'active',         label: 'Active',          desc: 'Open work, awaiting payment',   count: presetCounts.active },
          { key: 'completed_paid', label: 'Completed & Paid', desc: 'Fully closed repairs',           count: presetCounts.completed_paid },
          { key: 'all',            label: 'All',             desc: 'Show everything',               count: presetCounts.all },
        ].map(p => (
          <button key={p.key} onClick={() => setPresetWith(p.key)}
            data-testid={`preset-${p.key}`}
            title={p.desc}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide transition-colors border"
            style={{
              background: preset === p.key ? 'var(--primary-muted)' : 'transparent',
              borderColor: preset === p.key ? 'var(--primary)' : 'var(--border)',
              color: preset === p.key ? 'var(--primary)' : 'var(--text-muted)',
            }}>
            {p.label}
            <span className="font-['JetBrains_Mono'] text-[9px] px-1.5 py-0.5 rounded-full"
              style={{ background: preset === p.key ? 'var(--primary)' : 'var(--bg-darker)', color: preset === p.key ? '#fff' : 'var(--text-faint)' }}>
              {p.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filter Panel */}
      <FilterBar onClear={clearFilters} activeCount={activeCount}>
        {/* Search bar */}
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-faint)' }} />
          <input
            value={filters.unit_search}
            onChange={e => setF('unit_search', e.target.value)}
            placeholder="Search unit #, VIN, plate, repair ID..."
            data-testid="repair-search-input"
            className="w-full input-slate pl-9"
          />
        </div>
        {/* Row 1: core filters */}
        <div className="flex flex-wrap gap-3">
          <FilterSelect label="Status" value={filters.status} onChange={v => setF('status', v)} testId="filter-status"
            options={STATUSES.map(s => ({ value: s, label: s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) }))} />
          <FilterSelect label="Priority" value={filters.priority} onChange={v => setF('priority', v)} testId="filter-priority"
            options={PRIORITIES.map(p => ({ value: p, label: p.charAt(0).toUpperCase()+p.slice(1) }))} />
          <FilterSelect label="Category" value={filters.category} onChange={v => setF('category', v)}
            options={CATS.map(c => ({ value: c, label: c }))} />
          <FilterSelect label="Payment" value={filters.payment_state} onChange={v => setF('payment_state', v)}
            options={[{value:'unpaid',label:'Unpaid'},{value:'paid',label:'Paid'},{value:'overdue',label:'Overdue'}]} />
          <FilterSelect label="Stale" value={filters.stale_hours} onChange={v => setF('stale_hours', v)} options={STALE_OPTS} />
        </div>
        {/* Row 2: advanced */}
        <div className="flex flex-wrap gap-3">
          {user?.role !== 'customer' && (
            <FilterSelect label="Customer" value={filters.customer_id} onChange={v => setF('customer_id', v)}
              options={customers.map(c => ({ value: c.id, label: c.company_name }))} />
          )}
          <FilterInput label="Vendor / Shop" value={filters.vendor} onChange={v => setF('vendor', v)} placeholder="Shop name..." />
          <FilterInput label="Keyword" value={filters.keyword} onChange={v => setF('keyword', v)} placeholder="Search in description..." />
          <FilterDate label="From" value={filters.date_from} onChange={v => setF('date_from', v)} />
          <FilterDate label="To" value={filters.date_to} onChange={v => setF('date_to', v)} />
        </div>
      </FilterBar>

      {/* Pipeline (grouped) OR Flat list */}
      {viewMode === 'pipeline' ? (
        <div className="card-slate overflow-hidden" data-testid="repairs-pipeline-view">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
          ) : visibleRepairs.length === 0 ? (
            <div className="p-16 text-center">
              <Wrench size={28} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
              <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>No repair events match your filters</p>
            </div>
          ) : (
            <table className="w-full table-comfortable table-fixed">
              <colgroup>
                <col style={{ width: '4px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '15%' }} />
                <col />
                <col style={{ width: '95px' }} />
                <col style={{ width: '110px' }} />
                <col style={{ width: '48px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="p-0" />
                  <th>UNIT</th>
                  <th>CLIENT</th>
                  <th className="hidden md:table-cell">CATEGORY</th>
                  <th>ISSUE</th>
                  <th>PRIORITY</th>
                  <th className="hidden lg:table-cell num">EST.</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {REPAIR_PIPELINE_STAGES.map((stage, idx) => {
                  const items = repairsByStage[stage.key] || [];
                  if (items.length === 0) return null;
                  return (
                    <React.Fragment key={stage.key}>
                      <tr className="bg-[var(--bg-darker)]" data-testid={`stage-${stage.key}`}>
                        <td colSpan={8} className="py-2 px-4 border-y" style={{ borderColor: 'var(--border)' }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-[9px] font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--text-faint)' }}>STAGE {idx+1} / 7</span>
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[stage.key]||''}`}>
                                {stage.label}
                              </span>
                            </div>
                            <span className="text-[10px] font-['JetBrains_Mono'] font-bold" style={{ color: 'var(--text-primary)' }}>
                              {items.length} {items.length === 1 ? 'repair' : 'repairs'}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {items.map((r) => (
                        <tr key={r.id} onClick={() => navigate(`/ops/repairs/${r.id}`)} data-testid={`repair-row-${r.id}`}
                          className={`cursor-pointer ${PRIORITY_STRIP[r.priority]||'strip-low'}`}>
                          <td className="p-0" />
                          <td className="font-['JetBrains_Mono'] text-xs font-bold whitespace-nowrap" style={{ color: 'var(--primary)' }}>
                            {r.truck_unit_number||'N/A'}
                            {r.is_stale && <span className="ml-2 px-1.5 py-0.5 text-[8px] font-bold uppercase rounded" style={{ background: 'var(--error-muted)', color: 'var(--error)', border: '1px solid var(--error)' }}>STALE</span>}
                          </td>
                          <td className="text-sm font-['IBM_Plex_Sans'] truncate" style={{ color: 'var(--text-primary)' }}>{r.customer_name}</td>
                          <td className="text-xs font-['IBM_Plex_Sans'] hidden md:table-cell truncate" style={{ color: 'var(--text-muted)' }}><CategoryCell repair={r} /></td>
                          <td className="text-xs font-['IBM_Plex_Sans'] truncate" style={{ color: 'var(--text-muted)' }}>{r.issue_description}</td>
                          <td>
                            <span className={`text-[9px] font-bold uppercase font-['JetBrains_Mono'] ${r.priority==='emergency'?'text-[var(--error)]':r.priority==='high'?'text-orange-400':r.priority==='medium'?'text-[var(--primary)]':''}`} style={{ color: r.priority === 'low' ? 'var(--text-faint)' : undefined }}>
                              {r.priority}
                            </span>
                          </td>
                          <td className="text-xs font-['JetBrains_Mono'] hidden lg:table-cell num" style={{ color: 'var(--text-muted)' }}>{fmt(r.estimate_amount)}</td>
                          <td onClick={e => e.stopPropagation()}>
                            <button onClick={() => setDeleteTarget({ id: r.id, name: (r.truck_unit_number && r.truck_unit_number !== 'N/A') ? r.truck_unit_number : r.id.slice(0,8) })}
                              data-testid={`delete-repair-${r.id}`}
                              title="Delete repair"
                              className="p-1.5 rounded transition-colors"
                              style={{ color: 'var(--text-faint)' }}
                              onMouseEnter={e => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.background = 'var(--error-muted)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}>
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {(repairsByStage._other || []).length > 0 && (
                  <>
                    <tr className="bg-[var(--bg-darker)]">
                      <td colSpan={8} className="py-2 px-4 border-y" style={{ borderColor: 'var(--border)' }}>
                        <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wide" style={{ color: 'var(--text-faint)' }}>// OTHER · {repairsByStage._other.length}</span>
                      </td>
                    </tr>
                    {repairsByStage._other.map(r => (
                      <tr key={r.id} onClick={() => navigate(`/ops/repairs/${r.id}`)} className="cursor-pointer">
                        <td className="p-0" />
                        <td className="font-['JetBrains_Mono'] text-xs font-bold" style={{ color: 'var(--primary)' }}>{r.truck_unit_number||'N/A'}</td>
                        <td className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{r.customer_name}</td>
                        <td className="text-xs hidden md:table-cell truncate" style={{ color: 'var(--text-muted)' }}><CategoryCell repair={r} /></td>
                        <td className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{formatStatus(r.status)}</td>
                        <td><span className="text-[9px] uppercase">{r.priority}</span></td>
                        <td className="text-xs hidden lg:table-cell num font-['JetBrains_Mono']" style={{ color: 'var(--text-muted)' }}>{fmt(r.estimate_amount)}</td>
                        <td />
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      ) : (
      /* Flat List Table */
      <div className="card-slate overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
        ) : (
          <table className="w-full table-comfortable" data-testid="repairs-table">
            <thead>
              <tr>
                {['','UNIT','CLIENT','CATEGORY','ISSUE','STATUS','PRIORITY','EST.',''].map((h,i) => (
                  <th key={i} className={`${h===''?'w-1 p-0':''} ${['CLIENT','CATEGORY'].includes(h)?'hidden md:table-cell':''} ${['EST.'].includes(h)?'hidden lg:table-cell':''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRepairs.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16">
                  <Wrench size={28} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                  <p className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-faint)' }}>No repair events match your filters</p>
                </td></tr>
              ) : visibleRepairs.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/ops/repairs/${r.id}`)} data-testid={`repair-row-${r.id}`}
                  className={`cursor-pointer ${PRIORITY_STRIP[r.priority]||'strip-low'}`}>
                  <td className="w-1 p-0" />
                  <td className="font-['JetBrains_Mono'] text-xs font-bold" style={{ color: 'var(--primary)' }}>
                    {r.truck_unit_number||'N/A'}
                    {r.is_stale && <span className="ml-2 px-1.5 py-0.5 text-[8px] font-bold uppercase rounded" style={{ background: 'var(--error-muted)', color: 'var(--error)', border: '1px solid var(--error)' }}>STALE</span>}
                  </td>
                  <td className="text-sm font-['IBM_Plex_Sans'] hidden md:table-cell" style={{ color: 'var(--text-primary)' }}>{r.customer_name}</td>
                  <td className="text-xs font-['IBM_Plex_Sans'] hidden md:table-cell" style={{ color: 'var(--text-muted)' }}><CategoryCell repair={r} /></td>
                  <td className="text-xs font-['IBM_Plex_Sans'] max-w-[240px] truncate" style={{ color: 'var(--text-muted)' }}>{r.issue_description}</td>
                  <td>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[r.status]||STATUS_COLORS.open}`}>
                      {formatStatus(r.status)}
                    </span>
                  </td>
                  <td>
                    <span className={`text-[9px] font-bold uppercase font-['JetBrains_Mono'] ${r.priority==='emergency'?'text-[var(--error)]':r.priority==='high'?'text-orange-400':r.priority==='medium'?'text-[var(--primary)]':''}`} style={{ color: r.priority === 'low' ? 'var(--text-faint)' : undefined }}>
                      {r.priority}
                    </span>
                  </td>
                  <td className="text-xs font-['JetBrains_Mono'] hidden lg:table-cell" style={{ color: 'var(--text-muted)' }}>{fmt(r.estimate_amount)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <button onClick={() => setDeleteTarget({ id: r.id, name: (r.truck_unit_number && r.truck_unit_number !== 'N/A') ? r.truck_unit_number : r.id.slice(0,8) })}
                      data-testid={`delete-repair-${r.id}`}
                      title="Delete repair"
                      className="p-1.5 rounded transition-colors"
                      style={{ color: 'var(--text-faint)' }}
                      onMouseEnter={e => { e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.background = 'var(--error-muted)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; e.currentTarget.style.background = 'transparent'; }}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-lg">
          <DialogHeader><DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">New Repair Event</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 mt-2">
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Customer *</label>
              <select value={form.customer_id} onChange={e => { setForm(p=>({...p,customer_id:e.target.value,truck_id:''})); loadTrucks(e.target.value); }} required
                className="w-full select-slate">
                <option value="">Select customer...</option>
                {createCustomers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Unit *</label>
              <select value={form.truck_id} onChange={e => { setForm(p=>({...p,truck_id:e.target.value})); loadAlerts(e.target.value, form.customer_id); }} required
                className="w-full select-slate">
                <option value="">Select unit...</option>
                {createTrucks.map(t => <option key={t.id} value={t.id}>{t.unit_number} — {t.make} {t.model}</option>)}
              </select>
            </div>

            {/* Recent Repair Alerts */}
            {recentAlerts.length > 0 && (
              <div className="space-y-2">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>// RECENT REPAIR ALERTS</p>
                {recentAlerts.map((alert, i) => (
                  <div key={i} className={`p-3 rounded-md border text-xs font-['IBM_Plex_Sans'] ${alert.severity === 'critical' ? 'bg-[var(--error-muted)] border-[var(--error)] text-[var(--error)]' : 'bg-[var(--warning-muted)] border-[var(--warning)] text-[var(--warning)]'}`}>
                    <p className="font-bold mb-0.5">
                      {alert.severity === 'critical' ? '🚨' : '⚠️'} {alert.category?.replace(/_/g,' ')}
                      {alert.position_code && alert.position_code !== 'NONE' && ` — ${alert.position_code}`}
                      {alert.subtype && ` / ${alert.subtype.replace(/_/g,' ')}`}
                    </p>
                    <p className="opacity-80">
                      {alert.miles_ago != null ? `${alert.miles_ago.toLocaleString()} miles ago` : ''}{alert.miles_ago != null && alert.days_ago != null ? ' · ' : ''}{alert.days_ago != null ? `${alert.days_ago} days ago` : ''}
                      {alert.vendor_name ? ` · Vendor: ${alert.vendor_name}` : ''}
                    </p>
                    {(alert.threshold_miles || alert.threshold_days) && (
                      <p className="opacity-50 mt-0.5">
                        Client tracking window: {alert.threshold_miles?.toLocaleString()}mi / {alert.threshold_days}d
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Priority</label>
                <select value={form.priority} onChange={e => setForm(p=>({...p,priority:e.target.value}))}
                  className="w-full select-slate">
                  {PRIORITIES.map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Category (Primary)</label>
                <select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value, additional_categories: (p.additional_categories || []).filter(c => c !== e.target.value)}))}
                  data-testid="repair-category-select"
                  className="w-full select-slate">
                  {(CATS.length ? CATS : [form.category || 'Other']).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Additional categories — multi-select chips */}
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>
                Additional Categories <span style={{ color: 'var(--text-muted)' }}>(optional — tag every system worked on)</span>
              </label>
              <div className="flex flex-wrap gap-1.5" data-testid="additional-categories-picker">
                {CATS.filter(c => c !== form.category).map(c => {
                  const selected = (form.additional_categories || []).includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm(p => {
                        const cur = p.additional_categories || [];
                        const next = cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c];
                        return { ...p, additional_categories: next };
                      })}
                      data-testid={`additional-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                      className="text-[10px] font-['JetBrains_Mono'] px-2 py-1 rounded-full border transition-colors"
                      style={selected ? {
                        background: 'rgba(59, 130, 246, 0.18)',
                        borderColor: 'rgba(59, 130, 246, 0.55)',
                        color: 'rgb(96, 165, 250)',
                      } : {
                        background: 'transparent',
                        borderColor: 'var(--border)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {selected ? '✓ ' : '+ '}{c}
                    </button>
                  );
                })}
              </div>
              {(form.additional_categories || []).length > 0 && (
                <p className="text-[10px] mt-1.5 font-['JetBrains_Mono']" style={{ color: 'var(--text-faint)' }}>
                  {form.additional_categories.length} additional {form.additional_categories.length === 1 ? 'category' : 'categories'} selected
                </p>
              )}
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Issue Description *</label>
              <textarea value={form.issue_description} onChange={e => setForm(p=>({...p,issue_description:e.target.value}))} required rows={2}
                className="w-full input-slate resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Location *</label>
                <input type="text" value={form.location} onChange={e => setForm(p=>({...p,location:e.target.value}))} required placeholder="City, ST"
                  className="w-full input-slate" />
              </div>
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Mileage</label>
                <input type="number" value={form.mileage_at_repair} onChange={e => setForm(p=>({...p,mileage_at_repair:e.target.value}))}
                  className="w-full input-slate" />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>
                Vendor / Shop <span style={{ color: 'var(--text-muted)' }}>(optional — can assign later)</span>
              </label>
              <select
                value={form.assigned_vendor_id}
                onChange={e => {
                  const vid = e.target.value;
                  const v = vendorList.find(x => x.id === vid);
                  setForm(p => ({
                    ...p,
                    assigned_vendor_id: vid,
                    // If a known vendor is picked, auto-fill the name.
                    // Clearing the dropdown does NOT wipe any free-text name the
                    // user might have typed for an unlisted shop.
                    vendor_name: v ? v.name : p.vendor_name,
                  }));
                }}
                className="w-full select-slate"
                data-testid="repair-vendor-select">
                <option value="">— Assign later / unlisted shop —</option>
                {vendorList.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name}{v.city ? ` — ${v.city}, ${v.us_state || ''}` : ''}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={form.vendor_name}
                onChange={e => setForm(p=>({...p, vendor_name: e.target.value}))}
                placeholder="Or type a shop name (for unlisted vendors / phone-booked shops)"
                className="w-full input-slate mt-2"
                data-testid="repair-vendor-name" />
            </div>
            <button type="submit" disabled={saving} data-testid="create-repair-submit"
              className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
              {saving && <Loader2 size={14} className="animate-spin" />} Create Repair Event
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--error)' }} className="font-['Manrope']">Delete Repair</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-['IBM_Plex_Sans'] mt-2" style={{ color: 'var(--text-muted)' }}>
            Are you sure you want to delete repair <span className="font-bold font-['JetBrains_Mono']" style={{ color: 'var(--text-primary)' }}>{deleteTarget?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} style={{ background: 'var(--error)', color: 'white' }} data-testid="confirm-delete-repair">
              {deleting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
